import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Slider from "react-slick";
import { GenericCard } from "../components/GenericCard";
import { Button, Section, Container, Headline } from "../components/ui";
import { api, transformInserat } from "../services/api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Offers = ({
  carousel = false,
  showHeadline = true,
  headline = "Fahrzeuge im Verkauf",
  subscript = "Aktuelle Angebote",
  renderNavigateButton = false,
}) => {
  const navigate = useNavigate();
  const [inserate, setInserate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const fetchInserate = async () => {
      try {
        setLoading(true);
        const strapiInserate = await api.getInserate();
        const transformedInserate = strapiInserate
          .map(transformInserat)
          .filter(Boolean);
        setInserate(transformedInserate);
      } catch (err) {
        console.error("Error fetching inserate:", err);
        setError("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };

    fetchInserate();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleButtonClick = (inseratId) => {
    navigate(`/aktuelle-angebote/${inseratId}`);
  };

  const scrollToSlide = (index) => {
    const slider = document.querySelector(".offers-carousel .slick-slider");
    if (slider && slider.slick) {
      slider.slick.slickGoTo(index);
    }
    setCurrentSlide(index);
  };

  if (error) {
    return (
      <Section
        variant="primary"
        spacing="comfortable"
        margin={false}
        style={{ minHeight: "100vh" }}
      >
        <Container maxWidth="6xl">
          {showHeadline && (
            <div className="mb-8">
              <Headline
                variant="splitscreen"
                color="secondary"
                className="leading-tight mb-6 mt-0 text-left"
              >
                {headline}
              </Headline>
            </div>
          )}
          <div className="text-center">
            <p
              className="text-lg mb-4"
              style={{ color: "var(--color-highlight)" }}
            >
              {error}
            </p>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              size="medium"
            >
              Erneut versuchen
            </Button>
          </div>
        </Container>
      </Section>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Aktuelle Angebote | WTM Sportwagen - Porsche Fahrzeuge zum Verkauf
        </title>
        <meta
          name="description"
          content="Entdecken Sie unsere aktuellen Porsche Fahrzeuge zum Verkauf bei WTM Sportwagen. Geprüfte 911, 964, 993 und weitere Modelle mit Garantie und Service aus Gießen."
        />
        <meta
          name="keywords"
          content="Porsche kaufen, Porsche 911 kaufen, Porsche 964 993 996 997 Verkauf, Sportwagen kaufen Gießen, WTM Sportwagen Angebote"
        />

        {/* Open Graph tags */}
        <meta
          property="og:title"
          content="Porsche Fahrzeuge kaufen | WTM Sportwagen"
        />
        <meta
          property="og:description"
          content="Hochwertige Porsche Fahrzeuge zum Verkauf. Geprüfte Qualität und professioneller Service bei WTM Sportwagen in Gießen."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://wtm-sportwagen.com/aktuelle-angebote"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Porsche Fahrzeuge kaufen | WTM Sportwagen"
        />
        <meta
          name="twitter:description"
          content="Hochwertige Porsche Fahrzeuge zum Verkauf bei WTM Sportwagen in Gießen."
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href="https://wtm-sportwagen.com/aktuelle-angebote"
        />

        {/* Product/Inventory specific meta */}
        <meta name="product:condition" content="used" />
        <meta name="product:brand" content="Porsche" />
        <meta name="business:contact_data:locality" content="Gießen" />
      </Helmet>

      <Section
        variant="primary"
        spacing="tight_top_comfortable_bottom"
        margin={false}
        style={{
          minHeight: "100vh",
          backgroundColor: "var(--color-primary)",
        }}
      >
        <Container maxWidth="9xl">
          {showHeadline && (
            <div className="mb-8 px-0 sm:px-8 md:px-16 lg:px-24 xl:px-32 md:py-8">
              <Headline
                variant="splitscreen"
                color="secondary"
                className="leading-tight mb-0 mt-12 text-left"
              >
                {headline}
              </Headline>
            </div>
          )}
          {carousel ? (
            <div className="offers-carousel">
              <Slider
                dots={false}
                arrows={false}
                infinite={
                  inserate.length >
                  (windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 2 : 1)
                }
                speed={500}
                slidesToShow={3}
                slidesToScroll={1}
                autoplay={
                  inserate.length >
                  (windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 2 : 1)
                }
                autoplaySpeed={3000}
                beforeChange={(oldIndex, newIndex) => setCurrentSlide(newIndex)}
                swipe={
                  inserate.length >
                  (windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 2 : 1)
                }
                draggable={
                  inserate.length >
                  (windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 2 : 1)
                }
                responsive={[
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 2,
                      slidesToScroll: 1,
                      infinite: inserate.length > 2,
                      autoplay: inserate.length > 2,
                      swipe: inserate.length > 2,
                      draggable: inserate.length > 2,
                    },
                  },
                  {
                    breakpoint: 768,
                    settings: {
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      infinite: inserate.length > 1,
                      autoplay: inserate.length > 1,
                      swipe: inserate.length > 1,
                      draggable: inserate.length > 1,
                    },
                  },
                ]}
              >
                {inserate.map((inserat) => (
                  <div key={inserat.id} className="md:px-3">
                    <GenericCard
                      images={inserat.images}
                      headline={inserat.title}
                      info={[
                        { label: "BJ", value: inserat.year },
                        {
                          label: "KM",
                          value: `${inserat.mileage.toLocaleString()} km`,
                        },
                      ]}
                      price={
                        inserat.price > 0
                          ? `€${inserat.price.toLocaleString()}`
                          : undefined
                      }
                      buttonText="Details"
                      onButtonClick={() => handleButtonClick(inserat.id)}
                    />
                  </div>
                ))}
              </Slider>

              {/* Custom Dots Indicator - Only show when there are multiple slides */}
              {(() => {
                const slidesToShow =
                  windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 2 : 1;
                const totalSlides = Math.ceil(inserate.length / slidesToShow);
                const shouldShowDots = totalSlides > 1;

                return (
                  shouldShowDots && (
                    <div className="flex justify-center mt-6 space-x-2">
                      {Array.from({ length: totalSlides }).map((_, index) => (
                        <button
                          key={index}
                          onClick={() => scrollToSlide(index)}
                          className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                            index === currentSlide
                              ? "bg-white"
                              : "bg-gray-400 hover:bg-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  )
                );
              })()}
            </div>
          ) : (
            <div className="p-0 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl lg:max-w-9xl px-0 sm:px-8 md:px-16 lg:px-24 xl:px-32 mx-auto">
              {inserate.map((inserat) => (
                <GenericCard
                  key={inserat.id}
                  images={inserat.images}
                  headline={inserat.title}
                  info={[
                    // { label: "", value: inserat.model },
                    { label: "BJ", value: inserat.year },
                    {
                      label: "KM",
                      value: `${inserat.mileage.toLocaleString()} km`,
                    },
                  ]}
                  price={
                    inserat.price > 0
                      ? `€${inserat.price.toLocaleString()}`
                      : undefined
                  }
                  buttonText="Details"
                  onButtonClick={() => handleButtonClick(inserat.id)}
                />
              ))}
            </div>
          )}

          {/* Navigate Button */}
          {renderNavigateButton && inserate.length > 0 && (
            <div className="text-center mt-12">
              <Button
                onClick={() => navigate("/aktuelle-angebote")}
                variant="secondary"
                size="large"
              >
                Zu den Inseraten
              </Button>
            </div>
          )}

          {!loading && inserate.length === 0 && (
            <div className="text-center py-12">
              <p
                className="text-lg"
                style={{ color: "var(--color-secondary)" }}
              >
                Keine Angebote verfügbar
              </p>
            </div>
          )}
        </Container>
      </Section>
    </>
  );
};

export default Offers;
